import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { formatDate } from '../utils/tatCalculator';

const CourierTracking = ({ courierData }) => {
  if (!courierData) {
    return React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        color: 'text.secondary'
      }, 'No courier tracking information available')
    );
  }

  const InfoRow = ({ label, value }) => {
    return React.createElement(Grid, { container: true, spacing: 2, sx: { mb: 1 } },
      React.createElement(Grid, { item: true, xs: 4 },
        React.createElement(Typography, {
          variant: 'body2',
          color: 'textSecondary'
        }, label)
      ),
      React.createElement(Grid, { item: true, xs: 8 },
        React.createElement(Typography, {
          variant: 'body2',
          fontWeight: 'medium'
        }, value || 'N/A')
      )
    );
  };

  return React.createElement(Box, {
    sx: {
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      p: { xs: 3, sm: 4 },
      mb: 4,
      backgroundColor: 'background.paper'
    }
  },
    React.createElement(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        mb: 3
      }
    },
      React.createElement(LocalShippingIcon, {
        sx: { mr: 1.5, fontSize: '1.5rem' }
      }),
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Courier Tracking Details')
    ),
    React.createElement(Divider, { sx: { mb: 3 } }),
    React.createElement(Box, null,
      React.createElement(InfoRow, {
        label: 'AWB Number',
        value: courierData.awb || courierData.docketNo
      }),
      React.createElement(InfoRow, {
        label: 'Status',
        value: courierData.status || courierData.docketTrackingStatus
      }),
      React.createElement(InfoRow, {
        label: 'Origin',
        value: courierData.origin || courierData.consignorName
      }),
      React.createElement(InfoRow, {
        label: 'Destination',
        value: courierData.destination || courierData.consigneeName
      }),
      React.createElement(InfoRow, {
        label: 'Pickup Date',
        value: formatDate(courierData.pickupDate || courierData.bookingDate)
      }),
      React.createElement(InfoRow, {
        label: 'Expected Delivery',
        value: formatDate(courierData.expectedDelivery || courierData.edd)
      }),
      courierData.deliveredTime && React.createElement(InfoRow, {
        label: 'Delivered Time',
        value: formatDate(courierData.deliveredTime)
      }),
      courierData.receivedBy && React.createElement(InfoRow, {
        label: 'Received By',
        value: courierData.receivedBy
      }),
      courierData.delivered !== undefined && React.createElement(Box, { sx: { mt: 2 } },
        React.createElement(Chip, {
          label: courierData.delivered ? 'Delivered' : 'In Transit',
          size: 'small',
          sx: {
            backgroundColor: courierData.delivered ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 500,
            borderRadius: 2
          }
        })
      ),
      courierData.scans && React.createElement(Accordion, {
        sx: { mt: 2 }
      },
        React.createElement(AccordionSummary, {
          expandIcon: React.createElement(ExpandMoreIcon, null)
        },
          React.createElement(Typography, null, 'Scan History')
        ),
        React.createElement(AccordionDetails, null,
          (() => {
            // Parse scans if it's a string, otherwise use as-is
            let scansData = courierData.scans;
            if (typeof scansData === 'string') {
              try {
                scansData = JSON.parse(scansData);
              } catch (e) {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Invalid scan data format');
              }
            }

            // Ensure it's an array
            if (!Array.isArray(scansData)) {
              if (typeof scansData === 'object' && scansData !== null) {
                scansData = [scansData];
              } else {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Scan data is not in expected format');
              }
            }

            if (scansData.length === 0) {
              return React.createElement(Typography, {
                variant: 'body2',
                color: 'text.secondary'
              }, 'No scan history available');
            }

            // Get all unique keys from all scan objects
            const allKeys = [...new Set(scansData.flatMap(scan => Object.keys(scan)))];
            
            // Common field mappings for better display
            const fieldLabels = {
              date: 'Date',
              time: 'Time',
              datetime: 'Date & Time',
              timestamp: 'Timestamp',
              location: 'Location',
              city: 'City',
              status: 'Status',
              description: 'Description',
              action: 'Action',
              scanType: 'Scan Type',
              scan_type: 'Scan Type',
              remarks: 'Remarks',
              facility: 'Facility',
              facilityName: 'Facility Name',
              facility_name: 'Facility Name',
              statusDate: 'Status Date',
              statusTime: 'Status Time',
              statusType: 'Status Type',
              status_type: 'Status Type'
            };

            // Format field name for display
            const formatFieldName = (key) => {
              return fieldLabels[key.toLowerCase()] || key.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
            };

            return React.createElement(TableContainer, {
              component: Paper,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto'
              }
            },
              React.createElement(Table, {
                size: 'small',
                stickyHeader: true
              },
                React.createElement(TableHead, null,
                  React.createElement(TableRow, null,
                    allKeys.map(key => 
                      React.createElement(TableCell, {
                        key: key,
                        sx: {
                          fontWeight: 600,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }
                      }, formatFieldName(key))
                    )
                  )
                ),
                React.createElement(TableBody, null,
                  scansData.map((scan, index) => 
                    React.createElement(TableRow, {
                      key: index,
                      sx: {
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(0, 0, 0, 0.01)'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }
                    },
                      allKeys.map(key => 
                        React.createElement(TableCell, {
                          key: key,
                          sx: {
                            borderColor: 'divider',
                            fontSize: '0.875rem'
                          }
                        }, 
                          scan[key] !== null && scan[key] !== undefined 
                            ? (typeof scan[key] === 'object' 
                                ? JSON.stringify(scan[key]) 
                                : String(scan[key]))
                            : '-'
                        )
                      )
                    )
                  )
                )
              )
            );
          })()
        )
      ),
      courierData.eventList && React.createElement(Accordion, {
        sx: { mt: 2 }
      },
        React.createElement(AccordionSummary, {
          expandIcon: React.createElement(ExpandMoreIcon, null)
        },
          React.createElement(Typography, null, 'Event List')
        ),
        React.createElement(AccordionDetails, null,
          React.createElement(Typography, {
            variant: 'body2',
            sx: {
              whiteSpace: 'pre-wrap'
            }
          }, courierData.eventList)
        )
      )
    )
  );
};

export default CourierTracking;

